/**
 * Display density (blueprint §3 — docs/redesign/DESIGN_BLUEPRINT.md): auto | comfort | standard, persisted in
 * localStorage and applied as `data-display-scale` on <html>. The attribute
 * only affects `.rd-shell` (see tokens.css), so writing it eagerly here is
 * safe for production. `standard` exists for users who find ultra-wide
 * auto-zoom too large; `comfort` forces the large reading size everywhere.
 *
 * This module is only imported by the lazily-chunked DisplayScaleToggle and
 * the settings panels, so it lands in the Design-shared chunk
 * (vite.config.ts manualChunks). The boot-time init is deliberately NOT
 * here: it is inlined in src/main.tsx (kept logic-identical — update both)
 * because the main bundle must not eagerly import the lazy chunk.
 */

export type DisplayScale = 'auto' | 'comfort' | 'standard';

const STORAGE_KEY = 'vidi-display-scale';
const SCALES: readonly DisplayScale[] = ['auto', 'comfort', 'standard'];

export function getDisplayScale(): DisplayScale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'comfort' || stored === 'standard' || stored === 'auto') return stored;
  } catch {
    // storage unavailable (private mode) — fall back to auto
  }
  return 'auto';
}

export function applyDisplayScale(scale: DisplayScale): void {
  document.documentElement.dataset.displayScale = scale;
}

export function setDisplayScale(scale: DisplayScale): void {
  try {
    localStorage.setItem(STORAGE_KEY, scale);
  } catch {
    // non-fatal: the zoom still applies for this session
  }
  applyDisplayScale(scale);
}

/** Next value in the toggle's auto → comfort → standard cycle. */
export function nextDisplayScale(scale: DisplayScale): DisplayScale {
  return SCALES[(SCALES.indexOf(scale) + 1) % SCALES.length];
}
